import Autocomplete, { IAutocompleteOption } from '../common/autocomplete.tsx';
import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_FILTERED_COURSES } from '../../apollo/answers-view.ts';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { getAutocompleteKey } from '../../utils/common-utils.ts';
import { Sort } from '../../apollo/__generated__/graphql.ts';

type IProps = {
  disabled?: boolean;
  onChange: (course?: IAutocompleteOption) => void;
};

export const CourseSelect = ({ disabled, onChange }: IProps) => {
  const [course, setCourse] = useState<IAutocompleteOption>();
  const [courseName, setCourseName] = useState<string>();

  const { data: coursesData } = useQuery(GET_FILTERED_COURSES, {
    skip: disabled,
    variables: {
      paging: { limit: 50 },
      filter: {
        name: courseName,
      },
      sort: {
        createdAt: Sort.Desc,
      },
    },
  });

  const coursesOptions = useMemo(() => {
    return coursesData?.course?.getCourses?.results?.map((item) => ({
      title: item.name,
      key: item.id.toString(),
    }));
  }, [coursesData?.course?.getCourses?.results]);

  const setValue = useCallback(
    (value: IAutocompleteOption | IAutocompleteOption[]) => {
      if (Array.isArray(value)) return;
      if (getAutocompleteKey(value) === getAutocompleteKey(course)) return;
      onChange(value);
      setCourse(value);
    },
    [course, onChange],
  );

  const onClear = useCallback(() => {
    setCourse(undefined);
    setCourseName(undefined);
    onChange(undefined);
  }, [onChange]);

  return (
    <div className={'relative h-10 w-full'}>
      <Autocomplete
        value={course}
        disabled={disabled}
        className={
          'h-full w-full [&>div]:border [&>div]:border-[#dfe1e5] [&>div]:shadow-sm [&>div>input]:pr-16 [&>div>input]:placeholder:text-gray [&>div>input]:placeholder:opacity-60'
        }
        placeholder={'Select course'}
        leadingIcon={<MagnifyingGlassIcon className="h-4 w-4 text-gray" />}
        options={coursesOptions}
        setValue={setValue}
        onChangeValue={setCourseName}
      />
      {!!course && (
        <button
          aria-label="Clear course"
          className="absolute right-8 top-1/2 z-[1] flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-gray transition hover:bg-light-gray hover:text-black"
          onClick={onClear}
          type="button"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};
